import React, { useRef, useState, useEffect } from "react";
import i18n from "i18next";

import { fetchWrapper } from "../../helpers/fetchwrapper";
import FreigabeDialog from "../FreigabeDialog/FreigabeDialog";

import { baseUrl } from "../../helpers/constants";
import { IUserMedia } from "../../Models/IUserMedia";
import {
  Avatar,
  Card,
  CardHeader,
  CardPreview,
  MessageBar,
  Text,
  Caption1,
  Button,
  makeStyles,
  tokens,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from "@fluentui/react-components";
import { IUserMediaPosition } from "../../Models/IUserMediaPosition";
import {
  CheckRegular,
  DeleteRegular,
  EditRegular,
  LockClosedRegular,
  MoreHorizontal20Regular,
  MoreVertical20Regular,
  PeopleTeamRegular,
} from "@fluentui/react-icons";
import UserMediaEditDialog from "./UserMediaEditDialog";
import UserMediaDeleteDialog from "./UserMediaDeleteDialog";

const useStyles = makeStyles({
  main: {
    gap: "16px",
    display: "flex",
    flexWrap: "wrap",
  },

  card: {
    width: "400px",
    maxWidth: "100%",
    height: "fit-content",
  },

  caption: {
    color: tokens.colorNeutralForeground3,
  },

  smallRadius: { borderRadius: tokens.borderRadiusSmall },

  grayBackground: {
    backgroundColor: tokens.colorNeutralBackground3,
  },

  logoBadge: {
    padding: "5px",
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: "#FFF",
    boxShadow:
      "0px 1px 2px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)",
  },
});

interface IUserMediaProps {
  userProduktGuid: string;
  userMedia: IUserMedia | undefined;
  onRefresh(): void;
}

function UserMedia(props: IUserMediaProps) {
  const styles = useStyles();

  const [editUserMediaGuid, setEditUserMediaGuid] = useState<
    string | undefined
  >(undefined);

  const [deleteUserMediaGuid, setDeleteUserMediaGuid] = useState<
    string | undefined
  >(undefined);

  const [freigabeObjectGuid, setFreigabeObjectGuid] = useState("");

  const setAsVorschaubild = (
    userProduktGuid: string,
    userMediaGuid: string,
  ) => {
    fetchWrapper
      .getWithAuth(
        `${baseUrl}/api/UserProduct/SetVorschauPicture?UserProduktGuid=${userProduktGuid}&userMediaGuid=${userMediaGuid}`,
      )
      .then((result) => {
        props.onRefresh();
      });
  };

  useEffect(() => {
    if (freigabeObjectGuid == "" && props.userProduktGuid) {
      props.onRefresh();
    }
  }, [props.userProduktGuid, freigabeObjectGuid]);

  return (
    <div>
      {deleteUserMediaGuid && (
        <UserMediaDeleteDialog
          userMediaGuid={deleteUserMediaGuid}
          onDismiss={() => {
            setDeleteUserMediaGuid(undefined);
          }}
          onDelete={() => {
            props.onRefresh();
            setDeleteUserMediaGuid(undefined);
          }}
        />
      )}

      {editUserMediaGuid && (
        <UserMediaEditDialog
          userMediaGuid={editUserMediaGuid}
          onDismiss={() => {
            setEditUserMediaGuid(undefined);
          }}
          onSave={() => {
            props.onRefresh();
            setEditUserMediaGuid(undefined);
          }}
        />
      )}

      {freigabeObjectGuid != "" && (
        <FreigabeDialog
          onDismiss={() => {
            setFreigabeObjectGuid("");
          }}
          onSave={() => {
            props.onRefresh();
            setFreigabeObjectGuid("");
          }}
          isDialogOpen={freigabeObjectGuid != ""}
          objectGuid={freigabeObjectGuid}
          objectType="UserMedia"
          referenceGuid=""
        />
      )}

      <div className="lueb-WrapContainer200 lueb-MarginBottom20 lueb-MarginTop20">
        {props.userMedia?.positionen?.map((item: IUserMediaPosition) => {
          return (
            <Card className={styles.card}>
              <CardPreview
                className={styles.grayBackground}
                logo={<Avatar name={item.createdBy.fullName} />}
              >
                <img
                  className={styles.smallRadius}
                  src={`${baseUrl}/api/UserMedia/GetMedia?id=${item.userMediaGuid}`}
                  alt={item.docFileName}
                />
              </CardPreview>

              <CardHeader
                header={
                  <Text
                    weight="semibold"
                    truncate
                    wrap={false}
                    style={{ maxWidth: "150px" }}
                  >
                    {item.customerMediatext
                      ? item.customerMediatext
                      : item.docFileName}
                  </Text>
                }
                description={
                  item.model?.createdOn && (
                    <Caption1>
                      {new Date(item.model.createdOn).toLocaleDateString()}
                    </Caption1>
                  )
                }
                action={
                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <Button
                        appearance="transparent"
                        icon={<MoreVertical20Regular />}
                        aria-label="More actions"
                      />
                    </MenuTrigger>

                    <MenuPopover>
                      <MenuList>
                        <MenuItem
                          disabled={!item.canDeleteObject}
                          icon={<DeleteRegular />}
                          onClick={() => {
                            setDeleteUserMediaGuid(item.userMediaGuid);
                          }}
                        >
                          {i18n.t<string>("delete")}
                        </MenuItem>
                        <MenuItem
                          disabled={!item.canEditObject}
                          icon={<EditRegular />}
                          onClick={() => {
                            setEditUserMediaGuid(item.userMediaGuid);
                          }}
                        >
                          {i18n.t<string>("rename")}
                        </MenuItem>
                        <MenuItem
                          icon={
                            item.hasFreigaben ? (
                              <PeopleTeamRegular />
                            ) : (
                              <LockClosedRegular />
                            )
                          }
                          onClick={() => {
                            setFreigabeObjectGuid(item.userMediaGuid);
                          }}
                        >
                          {i18n.t<string>("shareObject")}
                        </MenuItem>
                        {!item.isVorschaubild && (
                          <MenuItem
                            icon={<CheckRegular />}
                            onClick={() => {
                              setAsVorschaubild(
                                props.userProduktGuid,
                                item.userMediaGuid,
                              );
                            }}
                          >
                            {i18n.t<string>("setAsVorschaubild")}
                          </MenuItem>
                        )}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                }
              />
            </Card>
          );
        })}
      </div>

      {props.userMedia?.positionen.length == 0 && (
        <div className="lueb-ContentBlock">
          <MessageBar style={{ marginTop: "5px" }} intent="info">
            {i18n.t<string>("noItemsFound")}
          </MessageBar>
        </div>
      )}
    </div>
  );
}

export default UserMedia;
